import {
  ChangeDetectionStrategy,
  booleanAttribute,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { TableColumnMeta } from '@shared/models/table.models';
import { AbookFilterSearchComponent } from './abook-filter-search.component';
import { observeBandSize } from './band-size';
import { ColumnPreferences } from './column-preferences';

export type ExportFormat = 'xlsx' | 'csv';

/**
 * The bar above a list page's table (R6): select-all and the count — or "n valgt" and the bulk
 * actions once rows are chosen — then Kolonner and Eksportér on the right. Replaced the
 * old `abook-column-selector` and `.bulk-actions` row (both removed). No red "0" badge.
 *
 * Below 840px of its own width, Kolonner and Eksportér fold into one ⋮ menu. A page's own figure
 * beside the count ("Udestående i alt") goes in `<span abookTableSummary>`.
 *
 * ```html
 * <abook-table-bar unit="bookinger" [total]="rows().length" [selectable]="true"
 *   [selectedCount]="selection().length" (selectAll)="toggleAll($event)"
 *   [columns]="columns" [availableColumns]="COLUMNS" pageName="Listevisning" (exported)="export($event)">
 *   <ng-container abookBulkActions>
 *     <button mat-button (click)="notify()"><mat-icon>mail</mat-icon> Send notifikation</button>
 *   </ng-container>
 * </abook-table-bar>
 * ```
 */
@Component({
  selector: 'abook-table-bar',
  imports: [
    AbookFilterSearchComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatPseudoCheckboxModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'abook-table-bar', '[attr.data-size]': 'size()' },
  templateUrl: './abook-table-bar.component.html',
  styleUrl: './abook-table-bar.component.scss',
})
export class AbookTableBarComponent {
  /** Rows matching the filters (all pages). */
  readonly total = input.required<number>();
  /** "bookinger", "brugere" — after the count. */
  readonly unit = input.required<string>();
  /** The unit for exactly one row — "1 bruger", not "1 brugere". Defaults to {@link unit}. */
  readonly unitOne = input<string | null>(null);
  readonly selectable = input(false, { transform: booleanAttribute });
  readonly selectedCount = input(0);
  /** The rows select-all acts on (usually the current page). */
  readonly selectableCount = input<number | null>(null);

  readonly columns = input<ColumnPreferences | null>(null);
  readonly availableColumns = input<readonly TableColumnMeta[]>([]);
  /** For "Huskes for Listevisning på denne computer". */
  readonly pageName = input('');
  readonly exportFormats = input<readonly ExportFormat[]>([]);
  /**
   * The result filter beside Kolonner and Eksportér: narrows the rows already fetched, at once.
   * `null` leaves it out. Shown only once there is a result to filter (or a filter text).
   */
  readonly resultFilter = input<string | null>(null);
  readonly resultFilterPlaceholder = input('Filtrér resultatet');
  /** Rows before the result filter; with it, the count reads "12 af 64 bookinger". */
  readonly unfilteredTotal = input<number | null>(null);

  readonly selectAll = output<boolean>();
  readonly exported = output<ExportFormat>();
  readonly resultFilterChange = output<string>();

  protected readonly showResultFilter = computed(() => {
    const filter = this.resultFilter();
    return filter !== null && (!!filter || (this.unfilteredTotal() ?? this.total()) > 0);
  });

  protected readonly size = observeBandSize();
  protected readonly folded = computed(() => this.size() === 'narrow' || this.size() === 'compact');

  protected readonly allSelected = computed(() => {
    const of = this.selectableCount() ?? this.total();
    return of > 0 && this.selectedCount() >= of;
  });
  protected readonly someSelected = computed(() => this.selectedCount() > 0 && !this.allSelected());

  protected readonly countText = computed(() => {
    if (this.selectedCount() > 0) return `${this.selectedCount()} valgt`;
    const of = this.unfilteredTotal();
    // "1 af 3 brugere" counts the 3; "1 bruger" alone is one.
    const unit =
      this.total() === 1 && (of === null || of === 1)
        ? (this.unitOne() ?? this.unit())
        : this.unit();
    return of !== null && of !== this.total()
      ? `${this.total()} af ${of} ${this.unit()}`
      : `${this.total()} ${unit}`;
  });
  protected readonly columnsText = computed(() => {
    const columns = this.columns();
    return columns
      ? `Kolonner ${columns.displayed().length}/${this.availableColumns().length}`
      : '';
  });

  protected readonly exportLabels: Record<ExportFormat, string> = { xlsx: 'Excel', csv: 'CSV' };

  protected isShown(column: string): boolean {
    return this.columns()?.displayed().includes(column) ?? false;
  }
}
